import ComponentManager from '@/Management/ComponentManager';
import WindowViewModel from '@/ViewModels/Controls/WindowViewModel';
import { IModalDetailViewWindowViewModelConstructorParameter, IRefreshable } from '#/DefaultViewExtensionInterfaces';
import ModalDetailViewViewModelT from '@/Components/ModalDetailView/ModalDetailViewViewModel';
import Messenger from '@/Utility/Messenger';
import ModalChangeHeaderTitleMessage from '@/Components/ModalDetailView/ModalChangeHeaderTitleMessage';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import DetailViewExitedMessage from '@/Messages/DetailViewExitedMessage';
import StorageHelper from '@/Utility/StorageHelper';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import ControlFactory from '@/Factories/ControlFactory';
import Class from '@/Class';

export default class ModalDetailViewWindowViewModel extends WindowViewModel {

    /**
     * Das ViewModel der beinhaltenden DetailView
     */
    public detailView: ModalDetailViewViewModelT;

    /**
     * Der aufrufende (Modal)DetailViewContext
     */
    private __caller: DetailViewContext;

    /**
     * Die aufrufende Assoziations-Factory, welches das Interfaces "IRefreshable" implementieren muss
     */
    private __callingFactory: IRefreshable;

    private isClosing: boolean;

    constructor(protected params: IModalDetailViewWindowViewModelConstructorParameter) {
        super($.extend(params,
            {
                allowMinimize: false,
                isResizable: true,
                isModal: true,
                isOpen: true,
                isVisible: true,
                locator: 'view',
                parentLocator: 'modal-window',
                width: StorageHelper.bind('ModalDetailViewWidth', '80%'),
                height: StorageHelper.bind('ModalDetailViewHeight', '80%'),
                headline: new LabelViewModelBuilder().build()
            }));

        if (params.caller instanceof DetailViewContext)
            this.__caller = this.paramOrDefault(params.caller, null);
        // instanceof bei TS, type-Prüfung für JS (Prototypenkette ist nicht vorhanden, sodass instanceof nicht funktioniert)
        else if (params.caller instanceof ControlFactory || (params.caller as Class)?.type.endsWith('Factory'))
            this.__caller = this.paramOrDefault(params.caller.context, null);

        this.detailView = this.createDetailView();
        this.registerForMessages();
        this.onClose = this.onCloseCallback.bind(this);
        this.__callingFactory = this.__caller?.getUIFactory<any>(this.params.contextRelationName) as IRefreshable;
        this.isClosing = false;
    }

    private registerForMessages(): void {
        Messenger.registerForMessageIdAndReceive('ModalChangeHeaderTitleMessage', (msg: ModalChangeHeaderTitleMessage) => {
            if (msg.context.identifier == this.detailView.currentContextIdentifier) {
                this.headline.caption(msg.prefix + ' ' + msg.title);
            }
        }, this);

        Messenger.registerForMessageIdAndReceive('DetailViewExitedMessage', (msg: DetailViewExitedMessage) => {
            if (msg.context.identifier == this.detailView.currentContextIdentifier) {
                // Erst wenn deleteDetailViewContext durchlaufen ist, dürfen wir die Datenquelle refreshen
                // Damit ist sichergestellt, dass ein verworfenes Objekt gelöscht ist und nicht irrtümlicherweise
                // (kurzzeitig) anzeigt wird
                RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded().then(() => {
                    if (!this.__callingFactory?.isDisposed)
                        this.__callingFactory.refresh();
                    this.isOpen(false);
                });
            }
        }, this);
    }

    private createDetailView(): ModalDetailViewViewModelT {
        const modalDv = ComponentManager.getViewModel<typeof ModalDetailViewViewModelT>('ModalDetailView');
        return new modalDv(
            {
                parent: this.params.parent,
                caller: this.__caller,
                pdTypeName: this.params.pdTypeName,
                pdoId: this.params.pdoId,
                contextPdoId: this.params.contextPdoId,
                contextPdTypeName: this.params.contextPdTypeName,
                contextRelationName: this.params.contextRelationName,
                contextParameters: this.params.contextParameters,
                detailViewContextCounter: this.params.detailViewContextCounter
            });
    }

    protected onCloseCallback(): void {
        if (this.isClosing)
            return;

        this.isClosing = true;

        $.when(this.detailView.currentContext()?.cancelModal())
            .always(() => {
                this.dispose();
            });
    }

    protected disposeInternal() {
        Messenger.unregisterForMessageId('ModalChangeHeaderTitleMessage', null, this);
        Messenger.unregisterForMessageId('DetailViewExitedMessage', null, this);
        super.disposeInternal();
    }
}

ComponentManager.setViewModel('ModalDetailViewWindow', ModalDetailViewWindowViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');